import { i18n } from '../i18n';
import { useToastStore } from '../store/useToastStore';
import { parseAxiosError } from './parseApiError';
import { translateApiError } from './translate';
import type { ApiError } from './types';

// Limit / feature-gate errors that warrant an "View plans" CTA in the toast.
// Codes match the dotted format from the backend's ErrorCodes.cs.
const UPGRADE_ERROR_CODES = new Set<string>([
  'subscriptionPlans.limitExceededOnCreate',
  'subscriptionPlans.limitExceededOnActivate',
  'subscriptionPlans.adminLimitExceededOnCreate',
  'subscriptionPlans.adminLimitExceededOnActivate',
  'subscriptionPlans.monthlyInvoiceLimitReached',
  'subscriptionPlans.featureNotAvailable',
]);

const SUBSCRIPTION_ROUTE = '/settings/subscription';

// Show a translated toast for an ApiError. If the error has per-field `errors`,
// nothing is shown — those should be surfaced inline on the form instead.
//
// 401s are typically handled by the auth interceptor (redirect to login), so
// callers can opt out via { skip401: true } to avoid double-handling.
//
// `codeActions` lets callers attach a CTA button to the toast for specific
// error codes (e.g. "Arbeitszeitmodell zuweisen" on
// planning.planningEntries.employeeWithoutWorkScheduleModel).
export function showApiError(
  error: unknown,
  opts: {
    skip401?: boolean;
    skipIfFieldErrors?: boolean;
    codeActions?: Record<string, { label: string; onClick: () => void }>;
  } = {},
): ApiError {
  const apiError = parseAxiosError(error);
  const { skip401 = true, skipIfFieldErrors = true } = opts;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[showApiError]', {
      status: apiError.status,
      code: apiError.code,
      hasFieldErrors: !!apiError.errors && Object.keys(apiError.errors).length > 0,
      fields: apiError.errors ? Object.keys(apiError.errors) : null,
    });
  }

  if (skip401 && apiError.status === 401) return apiError;
  if (
    skipIfFieldErrors &&
    apiError.errors &&
    Object.keys(apiError.errors).length > 0
  ) {
    return apiError;
  }

  const message = translateApiError(apiError);

  const codeAction = opts.codeActions?.[apiError.code];
  if (codeAction) {
    useToastStore.getState().addToast(message, 'error', {
      duration: 8000,
      action: codeAction,
    });
  } else if (UPGRADE_ERROR_CODES.has(apiError.code)) {
    useToastStore.getState().addToast(message, 'error', {
      duration: 8000,
      action: {
        label: i18n.t('common:upgrade'),
        // Hard navigation: this helper runs outside the React-Router context.
        onClick: () => window.location.assign(SUBSCRIPTION_ROUTE),
      },
    });
  } else {
    useToastStore.getState().addToast(message, 'error');
  }

  if (apiError.traceId && apiError.status >= 500) {
    // eslint-disable-next-line no-console
    console.error('[ApiError]', apiError.code, 'traceId:', apiError.traceId);
  }

  return apiError;
}
